import * as Collapsible from "@radix-ui/react-collapsible";
import * as Popover from "@radix-ui/react-popover";
import { parseISO } from "date-fns";
import { CalendarDays, Check, ChevronRight, Route, Search, User } from "lucide-react";
// biome-ignore lint/style/useImportType: Classic JSX runtime requires React.
import React from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Process } from "../../api/types";
import { AppSelect } from "../AppSelect";
import ProcessRunRow from "./ProcessRunRow";
import type { ProcessInstance } from "./processGraphModel";
import {
  processRecordingInstanceNumbersBySourceKey,
  processSourceInstanceKey,
} from "./processInstanceIdentity";
import {
  caseDay,
  processSourceInstanceIdentity,
  shouldShowProcessSourceUserName,
} from "./processInstanceLabels";
import { navigatePicker } from "./processPickerKeyboard";
import { matchRun } from "./processSearch";

export type ProcessInstanceOption = {
  instance: ProcessInstance;
  identity: ReturnType<typeof processSourceInstanceIdentity>;
};

/**
 * The one identity policy every instance-naming surface shares: per-recording numbering, user
 * names shown only when the process has more than one user, and the start time kept in the full
 * label. (The map's own `instanceOptionIdentity` deliberately diverges — no start time, and its
 * user-name policy reads the scoped instances.)
 */
export const useProcessSourceIdentity = (process: Process) => {
  const { t } = useTranslation();
  const recordingInstanceNumbers = useMemo(
    () => processRecordingInstanceNumbersBySourceKey(process.sources),
    [process.sources],
  );
  const showUserName = useMemo(
    () => shouldShowProcessSourceUserName(process.sources),
    [process.sources],
  );
  return useCallback(
    (source: ProcessInstance["source"]) => {
      const instanceNumber = recordingInstanceNumbers.get(processSourceInstanceKey(source)) ?? 1;
      return {
        ...processSourceInstanceIdentity({
          source,
          instanceLabel: t("process_instance_n", { num: instanceNumber }),
          fallbackRecordingName: t("recording_session"),
          showUserName,
        }),
        /** The instance's ordinal within its recording, for links into the recording analysis. */
        instanceNumber,
      };
    },
    [recordingInstanceNumbers, showUserName, t],
  );
};

/** Instances in scope that actually visit `stepInstanceIds`, labelled and in scope order. */
export const useProcessInstanceOptions = (
  process: Process,
  instancesInScope: ProcessInstance[],
  stepInstanceIds: Set<string>,
): ProcessInstanceOption[] => {
  const identityFor = useProcessSourceIdentity(process);
  return useMemo(
    () =>
      instancesInScope
        .filter(candidate => stepInstanceIds.has(candidate.id))
        .map(candidate => ({
          instance: candidate,
          identity: identityFor(candidate.source),
        })),
    [instancesInScope, identityFor, stepInstanceIds],
  );
};

export const ProcessInstanceList = ({
  options,
  onSelect,
}: { options: ProcessInstanceOption[]; onSelect: (id: string) => void }) => {
  const { t } = useTranslation();
  return (
    <ul className="space-y-1.5 rounded-xl border border-outline-strong bg-surface-sunken p-1.5">
      {options.map(option => (
        <li
          key={option.instance.id}
          className="flex items-center gap-3 rounded-lg border border-outline bg-surface px-3 py-2.5 shadow-sm"
        >
          <Route className="h-4 w-4 shrink-0 text-info" aria-hidden="true" />
          <ProcessRunRow run={option.instance} label={option.identity.instanceLabel} />
          <button
            type="button"
            onClick={() => onSelect(option.instance.id)}
            aria-label={t("open_instance_view_hint", {
              label: option.identity.fullLabel,
            })}
            className="shrink-0 rounded-full border border-outline px-2.5 py-1 text-xs text-text-mid hover:bg-muted-surface focus-visible:ring-2 focus-visible:ring-primary"
          >
            {t("view_instance_action")}
          </button>
        </li>
      ))}
    </ul>
  );
};

type ProcessInstancePickerProps = {
  process: Process;
  options: ProcessInstanceOption[];
  selectedInstanceId: string | null;
  onSelect: (id: string) => void;
  children: React.ReactNode;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
  label: string;
  /** Own the scroll lock when opened inside a dialog. */
  modal?: boolean;
};
export default function ProcessInstancePicker({
  process,
  options,
  selectedInstanceId,
  onSelect,
  children,
  align = "center",
  side = "bottom",
  label,
  modal = false,
}: ProcessInstancePickerProps) {
  const { t, i18n } = useTranslation();
  const [group, setGroup] = useState("none");
  const [collapsedGroups, setCollapsedGroups] = useState(new Set<string>());
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const content = useRef<HTMLDivElement>(null);
  const picked = useRef(false);
  const rows = options
    .map(option => ({
      option,
      match: matchRun(process, option.instance, query),
    }))
    .filter(row => !query.trim() || row.match)
    .sort((a, b) => (b.match?.score ?? 0) - (a.match?.score ?? 0));
  const groups = new Map<string, typeof rows>();
  for (const row of rows) {
    const source = row.option.instance.source;
    const key = group === "none" ? "" : group === "person" ? source.userId : source.recordingId;
    const entries = groups.get(key) ?? [];
    entries.push(row);
    groups.set(key, entries);
  }
  const groupKeys = [...groups.keys()].map(key => `${group}:${key}`);
  const allGroupsCollapsed = groupKeys.every(key => collapsedGroups.has(key));
  const formatDate = (day: string) =>
    new Intl.DateTimeFormat(i18n?.resolvedLanguage, {
      day: "numeric",
      month: "short",
      ...(day.slice(0, 4) !== String(new Date().getFullYear()) ? { year: "numeric" } : {}),
    }).format(parseISO(day));
  return (
    <Popover.Root
      modal={modal}
      open={open}
      onOpenChange={next => {
        setQuery("");
        setCollapsedGroups(new Set());
        setOpen(next);
      }}
    >
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          ref={content}
          align={align}
          side={side}
          sideOffset={8}
          collisionPadding={16}
          aria-label={label}
          onKeyDown={event => navigatePicker(event, content.current)}
          onCloseAutoFocus={event => {
            if (picked.current) {
              event.preventDefault();
              picked.current = false;
            }
          }}
          className="pd-theme z-[120] flex w-[28rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-outline bg-surface text-text shadow-lift"
          style={{
            maxHeight: "min(48rem, var(--radix-popover-content-available-height))",
          }}
        >
          <div className="shrink-0 border-b border-outline p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="text-xs font-semibold text-text-mid">{label}</h2>
              {group !== "none" && groups.size > 0 && (
                <button
                  type="button"
                  onClick={() => setCollapsedGroups(new Set(allGroupsCollapsed ? [] : groupKeys))}
                  className="shrink-0 rounded-md px-2 text-xs text-text-mid hover:bg-muted-surface hover:text-text focus-visible:ring-2 focus-visible:ring-info/20"
                >
                  {t(allGroupsCollapsed ? "processPage.expandAll" : "processPage.collapseAll")}
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search
                  className="absolute left-2.5 top-2.5 h-4 w-4 text-text-dim"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  value={query}
                  onChange={event => {
                    setQuery(event.target.value);
                    setCollapsedGroups(new Set());
                  }}
                  aria-label={t("processPage.searchRuns")}
                  placeholder={t("processPage.searchRuns")}
                  className="w-full min-w-0 rounded-lg border border-outline bg-surface py-2 pl-8 pr-2 text-sm focus:border-info focus:outline-none focus:ring-2 focus:ring-info/20"
                />
              </div>
              <div className="w-36 max-w-[45%] shrink-0">
                <AppSelect
                  contentClassName="pd-theme z-[121]"
                  label={t("processPage.groupBy")}
                  value={group}
                  onChange={value => {
                    setGroup(value);
                    setCollapsedGroups(new Set());
                  }}
                  choices={[
                    { id: "none", display: t("processPage.noGrouping") },
                    { id: "person", display: t("processPage.byPerson") },
                    { id: "session", display: t("processPage.bySession") },
                  ]}
                />
              </div>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5">
            {[...groups].map(([key, entries]) => {
              const groupKey = `${group}:${key}`;
              const expanded = !key || !collapsedGroups.has(groupKey);
              const source = entries[0].option.instance.source;
              return (
                <Collapsible.Root
                  key={groupKey}
                  asChild
                  open={expanded}
                  onOpenChange={value =>
                    setCollapsedGroups(previous => {
                      const next = new Set(previous);
                      if (value) next.delete(groupKey);
                      else next.add(groupKey);
                      return next;
                    })
                  }
                >
                  <section>
                    {key && (
                      <h3 className="sticky top-0 z-10 border-b border-outline bg-surface">
                        <Collapsible.Trigger className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-xs font-medium text-text-mid hover:bg-muted-surface focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-info/30">
                          <ChevronRight
                            className={`h-3.5 w-3.5 shrink-0 ${expanded ? "rotate-90" : ""}`}
                            aria-hidden="true"
                          />
                          {group === "person" ? (
                            <User className="h-4 w-4" aria-hidden="true" />
                          ) : (
                            <CalendarDays className="h-4 w-4" aria-hidden="true" />
                          )}
                          <span
                            className="min-w-0 flex-1 truncate"
                            title={
                              group === "session" ? (source.recordingName ?? undefined) : undefined
                            }
                          >
                            {source.userName || t("user")}
                            {group === "session" && ` · ${formatDate(caseDay(source))}`}
                          </span>
                          <span className="shrink-0 font-normal tabular-nums text-text-dim">
                            {t("n_instances", { count: entries.length })}
                          </span>
                        </Collapsible.Trigger>
                      </h3>
                    )}
                    <Collapsible.Content>
                      {entries.map(({ option, match }) => (
                        <button
                          type="button"
                          data-picker-option
                          key={option.instance.id}
                          aria-pressed={option.instance.id === selectedInstanceId}
                          onClick={() => {
                            picked.current = true;
                            onSelect(option.instance.id);
                            setOpen(false);
                          }}
                          className="flex w-full items-center gap-2 rounded-lg p-2 text-left hover:bg-muted-surface focus-visible:ring-2 focus-visible:ring-primary"
                        >
                          <span className="w-4 shrink-0">
                            {option.instance.id === selectedInstanceId && (
                              <Check size={14} aria-hidden="true" />
                            )}
                          </span>
                          <ProcessRunRow
                            run={option.instance}
                            label={option.identity.instanceLabel}
                            match={match}
                            hidePerson={group !== "none"}
                          />
                        </button>
                      ))}
                    </Collapsible.Content>
                  </section>
                </Collapsible.Root>
              );
            })}
            {rows.length === 0 && (
              <p className="p-4 text-sm text-text-dim">{t("no_results_match_filters")}</p>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
